import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPLETE_DETAIL_FIELD, SELECT_DETAIL_FIELD } from '@/consts/events';
import { RCta } from '../RCta';
import { WregRadioGroup } from '../WregRadioGroup';
import type { WregScreenEvent } from './wregScreenEvents';
import {
  FIELD_NAME,
  INTAKE_ABOUT_FIELDS,
  useIntakeErrors,
  type IntakeErrorKey,
} from './wregIntakeErrors';
import { WregIntakeShell } from './WregIntakeShell';
import { CalendarIcon, MaskedRow } from './WregIntakeRows';
import {
  dobIsUnlikely,
  dobMaskOptions,
  EMPTY_INTAKE,
  intakeAboutIsComplete,
  type WregIntakeForm,
} from './wregIntakeFields';

type Props = {
  dobError?: string | null;
  initial?: WregIntakeForm;
  onContinue: (patch: Partial<WregIntakeForm>) => void;
  onEvent?: WregScreenEvent;
};

const GENDERS: [string, string][] = [
  ['Female', 'wreg.intake.gender.f'],
  ['Male', 'wreg.intake.gender.m'],
];

const DOB_ID = 'wreg-int-dob';

const WregIntakeAbout = ({ dobError = null, initial, onContinue, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);
  const [dismissedDob, setDismissedDob] = useState<string | null>(null);
  const arrivalFocus = useRef(false);

  const valid = intakeAboutIsComplete(f);
  const { errors, touch, focus, blur, showError, errorId, validateBeforeSubmit } = useIntakeErrors({
    fields: INTAKE_ABOUT_FIELDS,
    form: f,
    valid,
    step: 'intake-about',
    t,
    onEvent,
  });

  const row = (field: IntakeErrorKey) => ({
    invalid: showError(field),
    errorId: errorId(field),
    error: showError(field) ? errors[field] : undefined,
    onFocus: focus(field),
    onBlur: blur(field),
  });

  const dobRow = {
    ...row('dob'),
    onFocus: () => {
      if (!arrivalFocus.current) focus('dob')();
    },
  };
  // The provider's error describes the submitted date, so it shows only while that date is in the field.
  const shownDobError = f.dob === initial?.dob ? dobError : null;
  const dobInvalid = Boolean(shownDobError) || dobRow.invalid;
  const dobWarned = !dobInvalid && dobIsUnlikely(f.dob) && dismissedDob !== f.dob;

  // The bounce lands here with no announcement, so focus reads the field's error out.
  // The flag keeps that focus out of analytics, since the member did not select the field.
  useEffect(() => {
    if (!dobError) return;
    arrivalFocus.current = true;
    document.getElementById(DOB_ID)?.focus();
    arrivalFocus.current = false;
  }, [dobError]);

  const pickGender = (gender: string) => {
    onEvent?.(SELECT_DETAIL_FIELD, { field: FIELD_NAME.gender });
    touch('gender')();
    setF((p) => ({ ...p, gender }));
    onEvent?.(COMPLETE_DETAIL_FIELD, { field: FIELD_NAME.gender, is_valid: true });
  };

  return (
    <WregIntakeShell
      step="intake-about"
      title={t('wreg.intake.about.title')}
      onEvent={onEvent}
      footer={
        <>
          <RCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            onClick={() =>
              onContinue({ dob: f.dob, gender: f.gender })
            }
          >
            {t('wreg.intake.next')}
          </RCta>
        </>
      }
    >
      <MaskedRow
        id={DOB_ID}
        label={t('wreg.intake.dob.label')}
        autoComplete="off"
        inputMode="numeric"
        lead={<CalendarIcon />}
        options={dobMaskOptions}
        value={f.dob}
        display={f.dob}
        mask={t('wreg.intake.dob.ph')}
        onAccept={(dob) => setF((p) => ({ ...p, dob }))}
        {...dobRow}
        invalid={dobInvalid}
        error={shownDobError ?? dobRow.error}
        warning={
          dobWarned
            ? {
                text: t('wreg.intake.dob.warn'),
                action: (
                  <button
                    type="button"
                    className="rg-warn-close"
                    // Keeps a pointer press from blurring the input, which would log a field
                    // complete and, with the refocus below, a second select.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setDismissedDob(f.dob);
                      // The button unmounts with the warning, which would drop focus to the page.
                      document.getElementById(DOB_ID)?.focus();
                    }}
                  >
                    {t('wreg.intake.dob.warn_close')}
                  </button>
                ),
              }
            : null
        }
      />
      <div className="rg-field">
        <span className="rg-label" id="wreg-int-gender-label">
          {t('wreg.intake.gender.label')}
        </span>
        <WregRadioGroup
          id="wreg-int-gender"
          value={f.gender === '' ? null : f.gender}
          onSelect={pickGender}
          options={GENDERS}
          labelledBy="wreg-int-gender-label"
          describedBy={showError('gender') ? errorId('gender') : undefined}
          invalid={showError('gender')}
        />
        {showError('gender') && (
          <span className="rg-error-hint" id={errorId('gender')}>
            {errors.gender}
          </span>
        )}
      </div>
    </WregIntakeShell>
  );
};

export { WregIntakeAbout };
